// ============================================================================
// English UI Copy — FULL SSOT (1:1 translation of he.ts)
// Text only — NO logic, NO URLs, NO React
// ============================================================================

export const en = {
  meta: {
    lang: 'en',
    langFull: 'en-US',
    dir: 'ltr' as const,
    name: 'English',
  },

  // ─────────────────────────────────────────────────────────────
  // WORDS
  // ─────────────────────────────────────────────────────────────

  words: {
    dj: {
      he: `DJ`,
      he_alt: `DJ`,
      he_plain: `DJ`,
      en: `DJ`,
    },
    music: 'Music',

    mc: `DJ`,

    events: {
      wedding: { one: `Wedding`, many: `Weddings`, to: `for a wedding` },
      henna: { one: `Henna`, many: `Henna`, to: `for a henna` },
      bar_bat: {
        one: `Bar/Bat Mitzvah`,
        many: `Bar/Bat Mitzvahs`,
        to: `for a Bar/Bat Mitzvah`,
      },
      generic: { one: `Event`, many: `Events`, to: `for an event` },
    },

    albums: {
      forum: `Forum`,
      articles: `Articles`,
      recommendations: `Reviews`,
      bars: `Bars`,
      flyers: `Flyers`,
    },

    phrases: {
      music_for_events: `music for events`,
      company_or_private: `company / private`,
    },
  },

  business: {
    name: 'DJ ELIOR MOR — Music for Events',

    address: {
      street: 'Mivtsa Yoav 16',
      city: 'Be’er Sheva',
      postal: '8445914',
      country: 'IL',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // BRAND
  // ─────────────────────────────────────────────────────────────
  brand: {
    firstName: `ELIOR`,
    short: `ELIOR MOR`,
    title: `DJ ELIOR MOR`,
  },

  metaDescription: {
    wedding:
      'Over 15 years of wedding experience: personal soundtrack, expert crowd reading, precise mixes and genre blending, full dance floor all night — Professional Wedding DJ',

    henna:
      'Over 15 years of henna experience: tradition blended with modern beats, colorful atmosphere and a packed dance floor — Professional Henna DJ',

    barBat:
      'Over 15 years of Bar/Bat Mitzvah experience: emotional entrance, music for teens and adults alike, full dance floor from start to finish — Professional DJ',

    generic:
      'Over 15 years of event experience: musical planning, crowd reading and genre blending, full dance floor throughout — Professional Event DJ',
  },

  // ─────────────────────────────────────────────────────────────
  // HOME
  // ─────────────────────────────────────────────────────────────

  home: {
    title: `Music for Events | DJ ELIOR MOR`,

    description:
      `ELIOR MOR — DJ with over 15 years of experience in weddings, henna ceremonies, Bar/Bat Mitzvahs and events. ` +
      `Expert crowd reading, genre blending and seamless mixes that keep the dance floor packed and create an unforgettable musical experience`,

    heroTitle: `ELIOR MOR — Music for Events`,

    sections: {
      section1Lines: (
        eventOne: string,
        mcLabel: string,
        brandTitle: string
      ) => [
        `Over 15 years creating perfect ${eventOne}s`,
        'Precise music and expert crowd reading',
        'Professional sound and seamless mixes',
        `${eventOne} with joyful, energetic vibes`,
        `Playlist tailored exactly to your ${eventOne}`,
        'A musical experience you will never forget',
        `${brandTitle} — ${mcLabel} for the perfect ${eventOne}`,
      ],

      section2DescriptionSuffix: (eventOne: string) =>
        `so every ${eventOne} becomes an unforgettable experience guests will talk about long after it ends`,

      section2AdditionalSuffix: '',

      section5SubtitleDesktop: (
        weddingOne: string,
        hennaOne: string,
        barBatOne: string,
        genericOne: string
      ) =>
        `Whether it's a ${weddingOne}, ${hennaOne}, ${barBatOne} or any other ${genericOne} — I tailor the music to your audience and the atmosphere you want. With precise timing, expert crowd reading and smart genre blending, the night flows naturally with high energy and a packed dance floor`,

      section5SubtitleMobile: (
        weddingMany: string,
        hennaMany: string,
        barBatOne: string,
        genericMany: string,
        genericOne: string
      ) =>
        `${weddingMany}, ${hennaMany}, ${barBatOne} and corporate ${genericMany} — I adapt the music to your crowd and keep the dance floor full until the end of the ${genericOne}.`,

      section6Title: (brandTitle: string) =>
        `Reviews for ${brandTitle}`,

      section6Subtitle: (genericOne: string) =>
        `What do clients say about the experience on the dance floor? Real reviews about music, energy and support from the first call to the end of the ${genericOne}`,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // SECTION 1 — OPEN
  // ─────────────────────────────────────────────────────────────

  section1: {
    targetPageLines: {
      base: [
        'Over 15 years of experience in all types of events',
        'Music built around your crowd’s energy',
        'Precise crowd reading and an irresistible dance vibe',
        'A full dance floor until the early hours',
      ],

      wedding: [
        'Over 15 years of wedding experience',
        'Music tailored for every wedding moment',
        'Personal soundtrack built just for you',
        'Seamless mixes and expert crowd reading',
        'Full musical planning and professional guidance',
        'A wedding guests will never stop talking about',
      ],

      henna: [
        'Emotional henna celebration with authentic vibes',
        'Colorful, joyful atmosphere',
        'Perfect blend of tradition and modern beats',
        'Drums and rhythms that energize the whole family',
        'Playlist tailored to your henna',
        'Emotional henna ceremony with electrifying atmosphere',
      ],

      bar_bat: [
        'Over 15 years of Bar/Bat Mitzvah experience',
        'Memorable grand entrance',
        'Full dance floor from the first song to the last',
        'Current hits teens and adults love',
        'Family-friendly joyful atmosphere',
        'A musical experience that stays in memory',
      ],

      event: [
        'Over 15 years of experience in all event types',
        'Precise music and expert crowd reading',
        'Professional sound and seamless mixes',
        'Energetic, joyful event atmosphere',
        'Playlist tailored exactly to your event',
        'A musical experience you will never forget',
      ],
    },
    hub: {
      city: {
        descriptionLines: (
          keyword: string,
          cityBet: string,
          brandTitle: string
        ) => [
          'Over 15 years of experience that speaks for itself',
          'Music tailored to every type of audience',
          'Ability to read the dance floor and play the perfect track',
          'A joyful atmosphere from the beginning to the end of the event',
          `${brandTitle} — ${keyword}${cityBet}`,
        ],
      
        extraHeadings: (keyword: string, cityBet: string) => [
          `How to choose ${keyword}${cityBet} the right way`,
          `Popular event types${cityBet}`,
        ],
      },
    
      region: {
        descriptionLines: (
          keyword: string,
          regionBet: string,
          brandTitle: string
        ) => [
          'Over 15 years of experience that speaks for itself',
          'Diverse music suitable for every audience type',
          'A dance floor filled with energy and joy',
          'A festive atmosphere that lasts all evening',
          `${brandTitle} — ${keyword}${regionBet}`,
        ],
      
        extraHeadings: (keyword: string, regionBet: string) => [
          `How to choose ${keyword}${regionBet}`,
          `Top cities${regionBet} to begin your search`,
        ],
      },
    
      home: {
        section1Lines: (
          eventOne: string,
          mcLabel: string,
          brandTitle: string
        ) => [
          `Over 15 years of experience ensuring a perfect ${eventOne}`,
          'Precise music and expert crowd reading',
          'Professional sound and seamless mixes',
          `${eventOne} with joyful, energetic vibes`,
          `Playlist tailored exactly to your ${eventOne}`,
          'A musical experience you will never forget',
          `${brandTitle} — ${mcLabel} for the perfect ${eventOne}`,
        ],
      },
    },
  },
    // ─────────────────────────────────────────────────────────────
  // SECTION 2 — ABOUT
  // ─────────────────────────────────────────────────────────────

  section2: {
    title: 'My Story',
    description:
    `With over 15 years behind the DJ booth, including a decade as resident DJ at the Forum club — I’m ELIOR MOR, delivering the right energy, expert crowd reading, and music that keeps the dance floor alive from the first song to the last.`,    additionalText:
      'For me, every event is a chance to energize the dance floor and create unforgettable moments. I read the crowd, blend genres, and surprise with tracks you haven’t heard in years — turning your event into an experience on another level',

    imageAltPortrait: `ELIOR MOR at the DJ booth`,
    imageAltHorizontal: `ELIOR MOR — DJ who knows how to ignite a party`,

    targetAdditionalByService: {
      wedding:
        'A wedding is a love story brought to life through music. I start by truly getting to know you — who you are, what excites you and how you imagine your wedding. From there we build a personal soundtrack, refine every moment and shape the atmosphere together to turn your dream into reality',

      henna:
        'Every henna celebration is a colorful party I build carefully from the very first moment. My goal is simple — joyful atmosphere, rhythms that lift everyone up and a packed dance floor. I blend tradition with modern beats to create unstoppable energy',

      bar_bat:
        'Every Bar/Bat Mitzvah is an opportunity to create special moments that connect everyone. My goal is simple — a party that feels natural for teens and enjoyable for adults. With the right balance of current hits and timeless classics, the result is an energetic celebration everyone feels part of',

      event:
        'Every event is an opportunity to create a precise musical experience tailored to the crowd and the occasion. Through real-time crowd reading, smooth transitions and smart genre blending, the atmosphere stays energetic all night long',
    },

    targetAdditionalSuffixByService: {
      wedding:
        'I bring your wedding a personal touch and precision that is felt in every moment',

      henna:
        'I add a special touch that turns your henna into a lively celebration',

      bar_bat:
        'I build a musical identity tailored exactly to your family and vibe',

      event:
        'I fine-tune the music so every event gets its own unique character',
    },

    fallbackSuffix:
      'This way your event gets a vibe perfectly suited to your audience',

    targetDescription: (
      baseDescription: string,
      djKeyword: string,
      cityBet: string
    ) =>
      `${baseDescription} I am ${djKeyword}${cityBet}, with excellent familiarity with local audiences and venues in the area`,

    targetAdditionalText: (
      base: string,
      cityBet: string,
      suffix: string
    ) => `${base} ${cityBet} ${suffix}`.replace(/\s+/g, ' ').trim(),
  },

  // ─────────────────────────────────────────────────────────────
  // SECTION 3 — GALLERY
  // ─────────────────────────────────────────────────────────────

  section3: {
    title: 'Let the Photos Speak',
    fallbackText: 'Select an album to see more details',
    albumsError: 'Unable to load albums. Please try again later.',
    filesError: 'Error loading files.',
    loading: 'Loading gallery…',

    galleryHeadings: {
      wedding: (keyword: string, cityBet: string) => [
        `Wedding photos${cityBet} — ceremony, first dance and dance floor`,
        `Wedding moments${cityBet} — energy on the dance floor`,
        `Happy couples and blazing sets — weddings${cityBet}`,
      ],
    
      henna: (_keyword: string, cityBet: string) => [
        `Henna gallery${cityBet} — color, circles and rhythm`,
        `Tradition and joy — henna celebrations${cityBet}`,
        `Drums and energy — real henna moments${cityBet}`,
      ],
    
      bar_bat: (_keyword: string, cityBet: string) => [
        `Bar/Bat Mitzvah${cityBet} — entrance, dance floor and atmosphere`,
        `Teens and families dancing — Bar/Bat Mitzvah${cityBet}`,
        `Peak moments and live photos — Bar/Bat Mitzvah${cityBet}`,
      ],
    
      event: (_keyword: string, cityBet: string) => [
        `Event highlights${cityBet} — packed dance floors`,
        `Musical style and energy — events${cityBet}`,
        `Event gallery${cityBet} — unforgettable moments`,
      ],
    },

    albumDescriptions: {
      weddings:
        'Photos from real weddings — ceremony, first dance and a blazing dance floor. A vibe that lifts every guest from start to finish',
    
      hennas:
        'Color, drums and circles — henna celebrations full of energy and joy, blending tradition with modern vibes',
    
      barBat:
        'Moments from Bar/Bat Mitzvahs — grand entrance, teen dance floor and electrifying atmosphere connecting everyone',
    
      forum:
        'Electrifying sets from the Forum club in Be’er Sheva, where I served as resident DJ for a decade. Atmosphere of 3,000 partygoers every weekend',
    
      articles:
        'Collection of media coverage, interviews and features — the stories behind the music',
    
      recommendations:
        'Real testimonials from clients — feedback, messages and thank-you notes after unforgettable nights',
    
      bars:
        'Sets from bars across the country — precise music for each crowd and energetic nights',
    
      flyers:
        'Collection of flyers from clubs and events — each tells the story of a memorable night',
    },

    albumTexts: {
      weddings:
        'Photos from real weddings — ceremony, first dance and a blazing dance floor. A vibe that lifts every guest from start to finish',

      hennas:
        'Color, drums and circles — henna celebrations full of energy and joy, blending tradition with modern vibes',

      barBat:
        'Moments from Bar/Bat Mitzvahs — grand entrance, teen dance floor and electrifying atmosphere connecting everyone',

      forum:
        'Electrifying sets from the Forum club in Be’er Sheva, where I served as resident DJ for a decade. Atmosphere of 3,000 partygoers every weekend',

      articles:
        'Collection of media coverage, interviews and features — the stories behind the music',

      recommendations:
        'Real testimonials from clients — feedback, messages and thank-you notes after unforgettable nights',

      bars:
        'Sets from bars across the country — precise music for each crowd and energetic nights',

      flyers:
        'Collection of flyers from clubs and events — each tells the story of a memorable night',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // SECTION 4 — CONTACT / CTA
  // ─────────────────────────────────────────────────────────────

  section4: {
    title: 'Upgrade Your Event',

    description:
      'Looking for a DJ for your event?\nHere you will find the winning combination of experience, energy and music',

    groupTitles: {
      contact: 'Contact',
      events: 'Event Categories',
      services: 'Additional Services',
    },
    eventsIntro: {
      default: `Music tailored to every type of event, private or corporate`,
    
      wedding: (keyword: string, cityBet: string) => {
        const loc = cityBet ? ` ${cityBet.trim()}` : '';
        return `${keyword}${loc} — from ceremony to party, music tailored to you and your guests`;
      },
    
      henna: (keyword: string, cityBet: string) => {
        const loc = cityBet ? ` ${cityBet.trim()}` : '';
        return `${keyword}${loc} — traditional rhythms blended with modern beats for a colorful family celebration`;
      },
    
      bar_bat: (keyword: string, cityBet: string) => {
        const loc = cityBet ? ` ${cityBet.trim()}` : '';
        return `${keyword}${loc} — current hits alongside timeless classics everyone enjoys`;
      },
    
      event: (keyword: string, cityBet: string) => {
        const loc = cityBet ? ` ${cityBet.trim()}` : '';
        return `Customized music for every kind of event, private or corporate`;
      },
    },
    servicesIntro: {
      wedding: (_keyword: string, cityBet: string) => {
        const loc = cityBet ? ` ${cityBet.trim()}` : '';
        return `Wedding add-ons${loc}: wedding lighting, professional sound and attractions.`;
      },
    
      henna: (_keyword: string, cityBet: string) => {
        const loc = cityBet ? ` ${cityBet.trim()}` : '';
        return `Henna add-ons${loc}: drummers, warm lighting and attractions`;
      },
    
      bar_bat: (_keyword: string, cityBet: string) => {
        const loc = cityBet ? ` ${cityBet.trim()}` : '';
        return `Bar/Bat Mitzvah add-ons${loc}: lighting, quality sound and attractions`;
      },
    
      event: (_keyword: string, cityBet: string) => {
        const loc = cityBet ? ` ${cityBet.trim()}` : '';
        return `Event add-ons${loc}: lighting, sound and attractions tailored to any event type`;
      },
    },
    eventsTexts: {
      wedding: (cityBet: string) =>
        `Wedding DJ${cityBet} — music perfectly tailored to the couple and guests`,
    
      henna: (cityBet: string) =>
        `Henna DJ${cityBet} — authentic tradition blended with modern beats`,
    
      bar_bat: (cityBet: string) =>
        `Bar/Bat Mitzvah DJ${cityBet} — hits for all generations`,
    
      event: (cityBet: string) =>
        `Event DJ${cityBet} — corporate or private, tailored to any style`,
    },
    groupTitlesSeo: {
      wedding: (keyword: string, cityBet: string, brand: string) => {
        const loc = cityBet ? ` ${cityBet.trim()}` : '';
    
        return {
          contact: `Contact ${brand}`,
          events: `More event categories${loc}`,
          services: `Wedding add-on services${loc}`,
          links: `Related pages for ${keyword}${loc}`,
        };
      },
    
      henna: (keyword: string, cityBet: string, brand: string) => {
        const loc = cityBet ? ` ${cityBet.trim()}` : '';
    
        return {
          contact: `Contact ${brand}`,
          events: `Other events I perform at${loc}`,
          services: `Henna add-on services${loc}`,
          links: `Related pages for ${keyword}${loc}`,
        };
      },
    
      bar_bat: (keyword: string, cityBet: string, brand: string) => {
        const loc = cityBet ? ` ${cityBet.trim()}` : '';
    
        return {
          contact: `Contact ${brand}`,
          events: `Popular event categories${loc}`,
          services: `Bar/Bat Mitzvah add-ons${loc}`,
          links: `Related pages for ${keyword}${loc}`,
        };
      },
    
      event: (keyword: string, cityBet: string, brand: string) => {
        const loc = cityBet ? ` ${cityBet.trim()}` : '';
    
        return {
          contact: `Contact ${brand}`,
          events: `Event types I cover${loc}`,
          services: `Event add-on services${loc}`,
          links: `Related pages for ${keyword}${loc}`,
        };
      },
    },
    contactSeo: {
      phone: `Call me — I’ll be happy to answer any question.`,
      whatsapp: `Send a WhatsApp message and I’ll get back to you shortly.`,
      email: `You can also email — send a message and I’ll respond soon.`,
      instagram: `Check Instagram — live videos and photos from the dance floor.`,
      facebook: `On Facebook you’ll find reviews and real event highlights.`,
      navigate: `Easy to reach — click for navigation in Google Maps.`,
    },
    contactButtons: {
      phone: 'Call',
      whatsapp: 'WhatsApp',
      email: 'Email',
      instagram: 'Instagram',
      facebook: 'Facebook',
      navigate: 'Navigate',
    },
    prefills: {
      wedding:
        'Hi Elior,\nI would love to get details about a wedding DJ.',
      henna:
        'Hello Elior,\nI’m interested in a henna DJ.',
      barBat:
        'Hi,\nLooking for a Bar/Bat Mitzvah DJ — can you send details?',
      generic:
        'Hello,\nI am looking for a DJ for a company/private event.',
    },
    blockCopy: {
      withCity: (
        keyword: string,
        cityName: string,
        firstName: string
      ) => ({
        description:
          `Looking for ${keyword} in ${cityName}?\nHere you’ll find the perfect combination of experience, energy and music`,
    
        whatsappPrefill:
          `Hi ${firstName}, I am interested in ${keyword} in ${cityName}`,
    
        linksHeading:
          `Related pages for ${keyword} in ${cityName}`,
      }),
    
      withoutCity: (
        keyword: string,
        eventName: string,
        firstName: string
      ) => ({
        description:
          `Looking for a DJ for your ${eventName}?\nHere you’ll find the perfect combination of experience, energy and music`,
      
        whatsappPrefill:
          `Hi ${firstName}, I am interested in a DJ for my ${eventName}`,
      
        linksHeading:
          `Related pages for DJ`,
      }),
    },
    services: {
      sound: {
        label: 'Sound System',
        description: 'Professional sound — clean and clear audio in any space.',
      },
      light: {
        label: 'Lighting',
        description: 'Dynamic lighting — transforms any dance floor.',
      },
      attractions: {
        label: 'Attractions',
        description: 'Event effects — confetti, heavy fog, balloons and more.',
      },
      recording: {
        label: 'Recording',
        description: 'Recording services — first dance, blessings or special entrances.',
      },
    },
  },
    // ─────────────────────────────────────────────────────────────
  // SECTION 5 — FAQ INTRO
  // ─────────────────────────────────────────────────────────────

  section5: {
    title: 'More Info',
    linksHeading: 'Related pages worth checking',
    subtitle:'Answers to common questions about music, planning and everything around your event.',
    moreLink: 'Read more questions',
    headingsSeo: {
      wedding: (keyword: string, cityBet: string) => [
        `Musical planning for weddings${cityBet} from the first call to the last song.`,
        `Elegant reception music that creates the perfect atmosphere.`,
        `Full ceremony guidance with precise song selection.`,
        `Music for the groom’s entrance, bride’s entrance and glass-breaking moment.`,
        `Smooth transition from ceremony to the opening dance.`,
        `An emotional slow dance tailored to the couple.`,
        `Music styles adapted to every stage of the evening.`,
        `${keyword}${cityBet} who accompanies you until the end of the night.`,
        `High-energy dance floor that keeps guests smiling.`,
      ],
    
      henna: (keyword: string, cityBet: string) => [
        `Henna celebration planning${cityBet} according to family traditions.`,
        `Music that creates a joyful and colorful atmosphere.`,
        `Grand entrance with drums and procession.`,
        `Traditional dance music for all generations.`,
        `Ceremony music for key henna moments.`,
        `Music for gift exchange and family circles.`,
        `Balanced mix of tradition and modern beats.`,
        `${keyword}${cityBet} who brings authentic energy to the celebration.`,
      ],
    
      bar_bat: (keyword: string, cityBet: string) => [
        `Bar/Bat Mitzvah planning${cityBet} with music for teens and adults.`,
        `Light reception music for guest arrival.`,
        `Grand entrance coordinated with chosen song.`,
        `Music for speeches, presentations and emotional moments.`,
        `High-energy dance sets for youth.`,
        `Balanced music suitable for all ages.`,
        `Interactive moments and special performances.`,
        `${keyword}${cityBet} who keeps the dance floor full all night.`,
      ],
    
      event: (
        keyword: string,
        cityBet: string,
        hasCity: boolean
      ) => {
        if (!hasCity) {
          return [
            `Every event begins with understanding your audience.`,
            `Music planning for reception, formal moments and party.`,
            `Soundtrack tailored to the event type.`,
            `Emotional music for special moments.`,
            `Gradual energy build toward dancing.`,
            `Smooth transitions that feel natural.`,
            `Professional sound suited to venue size.`,
            `${keyword} that creates a warm and unforgettable evening.`,
          ];
        }
      
        return [
          `Event planning${cityBet} tailored to your audience and venue.`,
          `Reception music that sets the mood.`,
          `Music for speeches, presentations and ceremonies.`,
          `Gradual transition to dance floor energy.`,
          `Real-time crowd reading throughout the event.`,
          `Sound calibrated for the venue.`,
          `${keyword}${cityBet} who accompanies you throughout the evening.`,
          `Guests leave with lasting memories.`,
        ];
      },
    },
    eventTypeDescription: {
      wedding: {
        mobile: (_city: string, cityBet: string) =>
          `Your wedding music${cityBet} is built around the story and atmosphere you want — from a welcoming reception, through the ceremony and emotional moments, to a high-energy party that connects all guests.`,
      
        desktop: (_city: string, cityBet: string) =>
          `To turn your wedding${cityBet} into an unforgettable experience, I create a personal soundtrack tailored to every stage of the event — from elegant reception music, through the ceremony and emotional moments, to a vibrant party that unites all guests on the dance floor.`,
      },
      event: {
        mobile: (_city: string, cityBet: string) =>
          `At every event${cityBet}, the music style is tailored to the audience. Smooth transitions, diverse playlists and strong energy keep everyone dancing.`,
      
        desktop: (_city: string, cityBet: string) =>
          `At every event${cityBet}, I adapt the sound and music style to the audience and event type. With the right mix of hits, classics and precise transitions, the atmosphere stays energetic until the final song.`,
      },
      bar_bat: {
        mobile: (_city: string, cityBet: string) =>
          `A Bar/Bat Mitzvah${cityBet} is a once-in-a-lifetime celebration. I combine current hits for teens with classics loved by the whole family.`,
      
        desktop: (_city: string, cityBet: string) =>
          `A Bar/Bat Mitzvah${cityBet} is a unique celebration. I blend modern youth hits with timeless party music for all generations, creating an energetic and joyful atmosphere.`,
      },
      henna: {
        mobile: (_city: string, cityBet: string) =>
          `A henna celebration${cityBet} is a colorful tradition. Authentic music, modern beats, drums and circles create powerful energy.`,
      
        desktop: (_city: string, cityBet: string) =>
          `A henna celebration${cityBet} blends tradition with vibrant party energy. Authentic rhythms, modern beats and live elements create a joyful family atmosphere.`,
      },
    },
  },

  // ─────────────────────────────────────────────────────────────
  // SECTION 6 — REVIEWS
  // ─────────────────────────────────────────────────────────────

  section6: {
    title: 'Reviews & Recommendations',

    subtitle:
      'What clients say about the experience on the dance floor, the energy and the service',

    aggregateText: 'Based on real reviews',

    readMore: 'Read all reviews',

    linkText: 'To the full reviews page',
    defaults: {
      title: (brandTitle: string) =>
        `Reviews and recommendations for ${brandTitle}`,
    
      subtitle: (
        brandShort: string,
        keyword: string,
        cityBet: string
      ) =>
        `What do clients say about ${brandShort}?\nReal reviews about ${keyword}${cityBet} — energy on the dance floor, crowd reading and professional service.`,
    },
    reviewsCount: (count: number) =>
      `Based on ${count} reviews`,
  
  },

  misc: {
    professional: 'Professional',
    recommended: 'Recommended',
  },
  // ─────────────────────────────────────────────────────────────
  // FOOTER
  // ─────────────────────────────────────────────────────────────

  footer: {
    copyright: (year: number) =>
      `© ${year} DJ ELIOR MOR — All rights reserved`,
    recommendedPages: 'Recommended pages',
    accessibilityStatement: 'Accessibility statement',
  },

  planning: {
    readMoreLabel: 'Full guide',
    previousLabel: 'Previous',
    nextLabel: 'Next',
    backHomeLabel: 'Back',
    articleEyebrow: (service: string) => `${service} - planning guide`,
    contactTitle: 'Want to plan the music correctly?',
    contactButtonLabel: 'Contact me',
    whatsappLabel: 'WhatsApp',
    phoneLabel: 'Phone call',
    parentLinkLabel: (serviceLabel: string) => `${serviceLabel} page`,
    parentContext: (articleTitle: string, serviceLabel: string) => ({
      before: `To make ${articleTitle} fit the full evening, it also helps to look at the `,
      linkLabel: serviceLabel,
      after: ' page and plan the music, timing, attractions and transitions as one clear flow.',
    }),
    whatsappText: (articleTitle: string) =>
      `Hi Elior, I read the guide "${articleTitle}" on your site and would like advice about music planning for my event.`,
    trustTitle: 'How this becomes real event planning',
    trustParagraphs: (articleTitle: string, serviceLabel: string, brandShort: string) => [
      `${articleTitle} is not just an idea on a checklist. In a real event it must connect to the crowd, venue, family, timing and the full musical flow. When I plan ${serviceLabel}, I look at the whole evening.`,
      `My experience as ${brandShort} helps decide where emotion should lead, where the tempo should rise, and where a short transition works better than another big moment.`,
      `Before the event I learn who the crowd is, which styles matter to you, what should not be played, and which moments must be exact. That creates a more personal and professional music plan.`,
    ],
    workTitle: 'What I check with you before finalizing the plan',
    workBullets: [
      'The event order and transitions between reception, ceremony, food and dance floor.',
      'Must-play songs, do-not-play songs and preferred styles.',
      'Crowd fit: family, friends, young guests, older guests or business audience.',
      'Coordination with venue, suppliers, musicians, attractions and sound equipment.',
    ],
    carouselLabel: (title: string) => `${title} - guides`,
    guideTitle: 'Event Planning Guide',
    guideSubtitle: 'All guides for planning weddings, hennas, bar/bat mitzvahs and private or business events in one place.',
    relatedTitle: 'More guides that can help',
    faqTitle: 'Frequently asked questions',
    authorTitle: 'Written from real event experience',
    authorText: 'This guide is based on more than 15 years of experience with weddings, hennas, bar/bat mitzvahs and events. The goal is to give practical planning advice, not generic ideas.',
    sections: {
      חתונה: { title: 'Wedding Planning', subtitle: 'Short guides for planning the key musical moments, from the chuppah to the slow dance.', keys: ['wedding_chuppah_song', 'wedding_timeline', 'wedding_attractions', 'wedding_musicians', 'wedding_slow_song', 'wedding_music'] },
      חינה: { title: 'Henna Planning', subtitle: 'Ideas for planning henna music around tradition, family and the right dance-floor energy.', keys: ['henna_entrance', 'henna_timeline', 'henna_attractions', 'henna_musicians', 'henna_music', 'henna_moroccan', 'henna_kurdish', 'henna_tunisian', 'henna_yemenite', 'henna_indian', 'henna_iraqi', 'henna_persian'] },
      'בר-מצווה': { title: 'Bar/Bat Mitzvah Planning', subtitle: 'Guides for entrances, recorded songs and musical moments for teens and family.', keys: ['bar_bat_entrance', 'bar_bat_timeline', 'bar_bat_attractions', 'bar_bat_musicians', 'bar_bat_recording', 'bar_bat_music'] },
      אירוע: { title: 'Event Planning', subtitle: 'Simple tools for planning music, timing and atmosphere for private and business events.', keys: ['event_entrance', 'event_music', 'event_music_timeline', 'event_attractions', 'event_musicians'] },
    },
    articles: {
      wedding_chuppah_song: { title: 'Choosing a Chuppah Entrance Song', sectionTitle: 'How to choose a chuppah entrance song', description: 'Choose a song that feels personal, emotional and timed correctly for the ceremony.', imageAlt: 'A couple entering the chuppah with emotional music', intro: 'A good chuppah song should match your personality, walking pace, ceremony setup and the feeling you want guests to remember.', bullets: ['Choose a song that feels like yours.', 'Check that the opening works well.', 'Match the length to the walking distance.', 'Coordinate the start point and transition with the DJ.'], cta: 'Want a chuppah entrance with precise timing? We can plan it together.' },
      wedding_timeline: { title: 'Wedding Timeline Planning', sectionTitle: 'How to build a wedding timeline', description: 'A clear wedding timeline connects reception, chuppah, slow dance and party without energy drops.', imageAlt: 'Wedding music timeline planning', intro: 'A timeline gives the evening structure and helps every supplier prepare for each transition.', bullets: ['Set reception, chuppah, food and dance times.', 'Leave buffers for natural delays.', 'Mark emotional highlights.', 'Match every transition to the desired energy.'], cta: 'A good music timeline keeps the wedding flowing from chuppah to final song.' },
      wedding_attractions: { title: 'Wedding Attractions', sectionTitle: 'How to choose wedding attractions', description: 'Wedding attractions should add experience without stealing focus from the dance floor.', imageAlt: 'Wedding attractions on the dance floor', intro: 'Photo booths, effects and dance-floor extras work best when they are timed and not overloaded.', bullets: ['Choose attractions that fit the crowd.', 'Make sure they do not block photos or dancing.', 'Coordinate timing with the DJ.', 'Prefer a few accurate ideas over clutter.'], cta: 'Attractions can support the party when they are planned correctly.' },
      wedding_musicians: { title: 'Wedding Musicians', sectionTitle: 'When to add live musicians to a wedding', description: 'Live musicians can add emotion to reception, chuppah or the opening of the dance floor.', imageAlt: 'Live musicians at a wedding reception', intro: 'Saxophone, violin, percussion or a reception band should be musically coordinated with the DJ.', bullets: ['Choose the right moment for musicians.', 'Coordinate songs and entry points.', 'Check sound setup at the venue.', 'Keep the live part focused and effective.'], cta: 'The right live musicians can make key moments feel alive.' },
      wedding_slow_song: { title: 'Choosing a Slow Dance Song', sectionTitle: 'How to choose a wedding slow dance song', description: 'Choose a slow dance song that feels intimate, natural and not forced.', imageAlt: 'Wedding dance floor during a slow dance', intro: 'The slow dance is short, but when planned well it stays memorable. The song should fit you and the level of drama you want.', bullets: ['Choose a song you genuinely love.', 'Avoid a song that is too long.', 'Decide if family and friends join midway.', 'Ask the DJ for a shortened version if needed.'], cta: 'We can choose the song and structure together.' },
      wedding_music: { title: 'Choosing Wedding Music', sectionTitle: 'How to choose music for a wedding', description: 'Wedding music should connect the couple, friends and both families through the right mix of styles.', imageAlt: 'DJ planning wedding music', intro: 'A wedding needs music for reception, chuppah, slow dance, party and after party. Start with the crowd: young friends, older family, religious guests, Mizrahi, Israeli, Latin, hip hop, nostalgia, trance, techno and bands you love.', bullets: ['Map the crowd by age, family side and religious or traditional needs.', 'List singers, bands and styles you and your family love.', 'Combine must-play songs with room for live crowd reading.', 'Build a flow from reception to dance floor and late-night energy.'], cta: 'The right wedding soundtrack balances personal taste and professional crowd reading.' },
      henna_entrance: { title: 'Henna Entrance', sectionTitle: 'How to plan a henna entrance', description: 'A henna entrance should open the evening with color, family and the right traditional rhythm.', imageAlt: 'Festive henna entrance', intro: 'Plan who enters, which song plays and how the moment moves into the ceremony.', bullets: ['Choose a song that fits the family tradition.', 'Set the entrance order.', 'Use drums or musicians only when they serve the moment.', 'Prepare a clean transition to the ceremony.'], cta: 'A strong entrance lifts the family from the first minute.' },
      henna_timeline: { title: 'Henna Timeline Planning', sectionTitle: 'How to build a henna timeline', description: 'A good henna timeline connects entrance, ceremony, outfits and dancing.', imageAlt: 'Henna event timeline planning', intro: 'Henna has many family moments, so the order should be clear while the energy stays alive.', bullets: ['Plan entrance, outfits, ceremony and dancing.', 'Leave time for blessings.', 'Decide where the tempo rises.', 'Avoid too many stops.'], cta: 'A precise timeline protects tradition and keeps the joy moving.' },
      henna_attractions: { title: 'Henna Attractions', sectionTitle: 'How to choose henna attractions', description: 'Henna attractions should strengthen the color and tradition without overloading the evening.', imageAlt: 'Colorful attractions at a henna', intro: 'Costumes, trays, drums and photo spots can add a lot when they fit the flow.', bullets: ['Match attractions to family tradition.', 'Keep room for circles and dancing.', 'Time each attraction inside the ceremony.', 'Choose who operates it in real time.'], cta: 'The right attractions add color without stopping the celebration.' },
      henna_musicians: { title: 'Henna Musicians', sectionTitle: 'Adding live musicians to a henna', description: 'Live musicians add authentic rhythm to entrances, ceremony and family circles.', imageAlt: 'Musicians and drums at a henna', intro: 'Drums, singers or traditional instruments should work together with the DJ and timeline.', bullets: ['Choose musicians who know the style.', 'Coordinate rhythms and entrance moments.', 'Check sound setup.', 'Balance performance and open dance floor.'], cta: 'Live musicians can give the henna a warm and authentic sound.' },
      henna_music: { title: 'Choosing Henna Music', sectionTitle: 'How to choose music for a henna', description: 'Henna music should honor tradition and still open a joyful dance floor for every age.', imageAlt: 'Choosing henna music with tradition and rhythm', intro: 'Good henna music starts with family tradition: Moroccan, Yemenite, Persian, Iraqi, Kurdish, Tunisian, Indian or mixed roots, then connects it with Mizrahi, Israeli, Latin, hip hop, nostalgia and current dance music.', bullets: ['Collect important ethnic and family songs.', 'Understand young guests, older guests and religious needs.', 'Blend authentic rhythms with modern dance-floor energy.', 'Plan the transition from ceremony to party.'], cta: 'Henna music should sound like your family and still lift the whole room.' },
      henna_moroccan: { title: 'Moroccan Henna', sectionTitle: 'How to plan a joyful Moroccan henna', description: 'Build a colorful Moroccan henna with tradition, rhythm and a happy dance floor.', imageAlt: 'A joyful dance floor at a Moroccan henna', intro: 'Moroccan henna music should honor tradition and still open a modern, happy dance floor.', bullets: ['Plan entrances and outfit changes.', 'Mix Moroccan rhythms with current hits.', 'Leave room for family songs.', 'Coordinate ceremony highlights with the DJ.'], cta: 'Let us build a soundtrack that knows the tradition and lifts the floor.' },
      henna_kurdish: { title: 'Kurdish Henna', sectionTitle: 'Music planning for a Kurdish henna', description: 'Connect family warmth, traditional rhythms and a real party.', imageAlt: 'Family celebration on a Kurdish henna dance floor', intro: 'In a Kurdish henna, the family should feel at home while every guest can join the dance floor.', bullets: ['Collect important traditional songs.', 'Build a clear ceremony flow.', 'Blend circles and familiar hits.', 'Leave room for family requests.'], cta: 'We can plan music that respects tradition and keeps the party alive.' },
      henna_tunisian: { title: 'Tunisian Henna', sectionTitle: 'How to build a Tunisian henna with the right pace', description: 'Ceremony, family and joy with music that carries the evening.', imageAlt: 'Music and dancing at a Tunisian henna', intro: 'A Tunisian henna works best when traditional moments and open celebration flow naturally.', bullets: ['Prepare must-play family songs.', 'Set timing for blessings and entrances.', 'Blend Tunisian rhythms with current songs.', 'Keep transitions short.'], cta: 'We can plan the family moments and the dance floor together.' },
      henna_yemenite: { title: 'Yemenite Henna', sectionTitle: 'Planning a Yemenite henna with joyful music', description: 'Give tradition space and continue into an energetic party.', imageAlt: 'Yemenite henna with music and dancing', intro: 'Good planning moves from Yemenite tradition into an updated dance floor without losing the character.', bullets: ['Choose entrance and ceremony songs.', 'Decide when the party starts.', 'Combine Yemenite, Mizrahi and current hits.', 'Make sure the sound fits ceremony and dancing.'], cta: 'Authentic and energetic can work together with the right plan.' },
      henna_indian: { title: 'Indian Henna', sectionTitle: 'How to plan a colorful Indian henna', description: 'Indian henna brings color, family ceremony, outfits and joyful music together.', imageAlt: 'Colorful Indian henna with music and dancing', intro: 'A good Indian henna connects tradition, entrances, outfits and a dance floor that feels natural for the whole crowd.', bullets: ['Choose entrance and ceremony songs that fit the family.', 'Plan entrances, outfits and blessing moments.', 'Blend Indian rhythms with Mizrahi, Israeli and current hits.', 'Prepare a clear transition from ceremony to dancing.'], cta: 'We can build a colorful, respectful and energetic henna soundtrack together.' },
      henna_iraqi: { title: 'Iraqi Henna', sectionTitle: 'Music planning for an Iraqi henna', description: 'Iraqi henna is warm, family-focused and rooted in songs that connect generations.', imageAlt: 'Iraqi henna with family and dancing', intro: 'The music should leave room for family songs, blessings and circles, then move naturally into a party for all guests.', bullets: ['Collect important Iraqi and family songs.', 'Set the order of entrance, ceremony and circles.', 'Blend Iraqi, Mizrahi and current music.', 'Keep the move into dancing short and clean.'], cta: 'Let us plan the balance between tradition, family and a live dance floor.' },
      henna_persian: { title: 'Persian Henna', sectionTitle: 'How to build a Persian henna with the right pace', description: 'Persian henna combines elegance, family ceremony and joyful music.', imageAlt: 'Elegant Persian henna with music and joy', intro: 'With the right plan, Persian tradition gets its place while the dance floor still feels current, happy and open to everyone.', bullets: ['Choose meaningful Persian songs for entrances.', 'Plan ceremony moments without long stops.', 'Blend Persian, Mizrahi, Israeli and international hits.', 'Coordinate highlights and transitions with the DJ.'], cta: 'We can make the evening respectful, joyful and smooth.' },
      bar_bat_recording: { title: 'Recording Songs for Bar/Bat Mitzvah', sectionTitle: 'Recording a song for a bar or bat mitzvah', description: 'A recorded song can make an entrance, video or family moment especially moving.', imageAlt: 'Microphone and song recording for a bar or bat mitzvah', intro: 'A recorded song works best when the lyrics are personal and the idea stays simple.', bullets: ['Choose a backing track that fits the voice.', 'Write short, clear lyrics.', 'Leave time for practice.', 'Coordinate when the song plays.'], cta: 'We can make the recorded song feel polished and emotional.' },
      bar_bat_entrance: { title: 'Bar/Bat Mitzvah Entrance', sectionTitle: 'How to plan a bar or bat mitzvah entrance', description: 'Give the child a central moment without making it overdone.', imageAlt: 'Emotional bar or bat mitzvah entrance', intro: 'The entrance should fit the child, whether dramatic, light, short or joyful.', bullets: ['Choose a song the child loves.', 'Decide who enters together.', 'Keep it short and clear.', 'Connect it to the next event moment.'], cta: 'We can build an entrance that fits the child exactly.' },
      bar_bat_timeline: { title: 'Bar/Bat Mitzvah Timeline Planning', sectionTitle: 'How to build a bar or bat mitzvah timeline', description: 'A good timeline balances entrance, blessings, food and a dance floor for teens and family.', imageAlt: 'Bar or bat mitzvah timeline planning', intro: 'The challenge is keeping the teens engaged while the family still feels included.', bullets: ['Set entrance, video, blessings and food times.', 'Open the dance floor early enough.', 'Coordinate games with the DJ.', 'Avoid a long block of speeches.'], cta: 'The right timeline keeps energy high for every age.' },
      bar_bat_attractions: { title: 'Bar/Bat Mitzvah Attractions', sectionTitle: 'How to choose bar or bat mitzvah attractions', description: 'Attractions should fit the child, friends and family without taking over the event.', imageAlt: 'Attractions for a bar or bat mitzvah', intro: 'Photo stations, dancers, games and special entrances work best when they match the child.', bullets: ['Choose something the child truly likes.', 'Check that it fits the age group.', 'Time it without breaking the dance floor.', 'Prefer one strong attraction over scattered ideas.'], cta: 'The right attraction makes the event more personal.' },
      bar_bat_musicians: { title: 'Bar/Bat Mitzvah Musicians', sectionTitle: 'When to add musicians to a bar or bat mitzvah', description: 'Live musicians can upgrade the entrance, reception or a special family moment.', imageAlt: 'Musicians at a bar or bat mitzvah', intro: 'Live musicians work best in a short, focused moment that supports the young crowd.', bullets: ['Choose an instrument that fits the event.', 'Keep the live moment short.', 'Coordinate songs and sound with the DJ.', 'Use musicians to strengthen the party, not replace it.'], cta: 'The right live moment adds celebration without making the event heavy.' },
      bar_bat_music: { title: 'Choosing Bar/Bat Mitzvah Music', sectionTitle: 'How to choose music for a bar or bat mitzvah', description: 'Music for a bar or bat mitzvah must work for teens, parents and older family together.', imageAlt: 'Choosing music for a bar or bat mitzvah', intro: 'This event has several audiences: classmates want current hits, parents want a pleasant and respectful event, and family needs to feel included. The playlist should mix pop, hip hop, Mizrahi, Latin, nostalgia, trance, techno and religious music when relevant.', bullets: ['Ask the child which singers, bands and styles they love.', 'Check what the family enjoys: Israeli, Mizrahi, religious, nostalgia or bands.', 'Mix teen hits with family moments, entrances and games.', 'Prepare a do-not-play list for age or religious sensitivity.'], cta: 'Good music keeps the child in the center and the family fully connected.' },
      event_music: { title: 'Choosing Music for an Event', sectionTitle: 'How to choose music for an event', description: 'Choose music that fits the audience, venue and goal of the evening.', imageAlt: 'DJ planning music for an event', intro: 'Event music is more than a playlist. It is about crowd, purpose, timing and energy.', bullets: ['Define the audience and atmosphere.', 'Choose important songs.', 'Leave room for live crowd reading.', 'Prepare a do-not-play list.'], cta: 'Good music planning turns background sound into a memorable evening.' },
      event_entrance: { title: 'Event Entrance', sectionTitle: 'How to plan an event entrance', description: 'An event entrance gives the evening a clear and festive opening.', imageAlt: 'Festive event entrance', intro: 'Even a general event can use a planned entrance to introduce hosts, guests of honor or the first dance-floor moment.', bullets: ['Choose a song that fits the event.', 'Decide who enters and when.', 'Coordinate lighting, announcement and music.', 'Keep it short and connected to the next moment.'], cta: 'A good entrance creates a clear start and sets the atmosphere.' },
      event_attractions: { title: 'Event Attractions', sectionTitle: 'How to choose attractions for an event', description: 'Attractions should fit the audience, venue and purpose of the evening.', imageAlt: 'Attractions at a private or business event', intro: 'Attractions can create interest, photos and memory when they fit the program.', bullets: ['Match the attraction to the crowd.', 'Check electricity, space and setup time.', 'Coordinate timing with the DJ.', 'Make sure it supports the event flow.'], cta: 'Good attractions add value when they are timed well.' },
      event_musicians: { title: 'Event Musicians', sectionTitle: 'Adding live musicians to an event', description: 'Live musicians add presence to reception, a short ceremony or a highlight moment.', imageAlt: 'Live musicians with a DJ at an event', intro: 'Reception bands, saxophone, violin or percussion should support the DJ instead of competing with the set.', bullets: ['Choose musicians by atmosphere.', 'Coordinate repertoire and entry points.', 'Check sound with the venue.', 'Use musicians where they add the most.'], cta: 'The right live musicians give the event an impressive live layer.' },
      event_music_timeline: { title: 'Planning an Event Timeline', sectionTitle: 'How to build an event timeline', description: 'A clear timeline helps the event flow through reception, speeches, highlights, food and dancing without energy drops.', imageAlt: 'Planning an event timeline', intro: 'With a clear timeline, every supplier knows when to step in and the DJ can keep the evening natural.', bullets: ['Split the event into stages.', 'Define highlights in advance.', 'Leave buffers for changes.', 'Coordinate with the venue and suppliers.'], cta: 'We can build the timeline around the venue, crowd, suppliers and content.' },
    },
  },
  // ─────────────────────────────────────────────────────────────
  // SEO FAQ — SERVICES
  // ─────────────────────────────────────────────────────────────

  menu: {
    sections: [
      { id: 'home', label: 'Home' },
      { id: 'story', label: 'My Story' },
      { id: 'gallery', label: 'Gallery' },
      { id: 'contact', label: 'Contact' },
      { id: 'info', label: 'More Info' },
      { id: 'reviews', label: 'Reviews' },
      { id: 'planning', label: 'Planning' },
    ],
  },

  seo: {
    faq: {
      wedding: [
        {
          q: `What does a wedding DJ service include {city}?`,
          a: `The service includes personal consultation and building a soundtrack tailored especially for you, professional guidance throughout the entire process, sharp crowd reading, precise mixes and genre blending, sound and energy that guarantee a joyful wedding from the very first moment to the last song.`,
        },
        {
          q: `How much does a wedding DJ cost {city}?`,
          a: `{city} The price range is usually between ₪4,000–₪9,000, depending on the wedding size, date and equipment.`,
        },
        {
          q: `How do you choose a wedding DJ {city}?`,
          a: `It is important to choose a DJ with proven wedding experience, strong crowd-reading ability and personal chemistry with the couple. It is recommended to check reviews, watch videos from real events and feel that someone truly understands your style and can lead the evening professionally.`,
        },
        {
          q: `Does a DJ {city} also provide lighting equipment?`,
          a: `Most venues already include basic lighting for the dance floor. For those interested, advanced stage lighting and design lighting can be added and customized to the event’s character. I work with leading suppliers in the field, offering a wide range of lighting and stage design options, with pricing depending on the chosen setup.`,
        },
        {
          q: `Can live musicians be incorporated into a wedding {city}?`,
          a: `Absolutely. Live musicians such as a saxophonist, violinist, percussionist or bouzouki player can be added to create a unique and emotional atmosphere.`,
        },
        {
          q: `How long does a DJ perform at a wedding?`,
          a: `Performance duration is determined in advance according to the event schedule and can range from two hours to unlimited time, depending on the couple’s preference and how the event unfolds.`,
        },
        {
          q: `Is a local DJ {city} familiar with the venues?`,
          a: `In most cases yes. Familiarity with local venues allows smooth coordination with the venue staff, accurate sound and lighting adjustments to the space, and proper preparation for the event schedule — contributing to a better flow and a more professional experience throughout the evening.`,
        },
        {
          q: `What do wedding DJ packages include {city}?`,
          a: `The basic package includes arrival with professional DJ equipment and performance throughout the event. Additional upgrades can include custom DJ booths, after-party sets, slow-dance packages, premium options, special attractions, live musicians and more — all tailored to your event style.`,
        },
        {
          q: `Is backup equipment available?`,
          a: `Yes, a professional DJ always arrives with backup gear and an emergency plan.`,
        },
        {
          q: `How do you choose songs for the ceremony?`,
          a: `Song selection is done together with the couple and tailored personally. Typically this includes a song for the groom’s entrance, the bride’s entrance and the glass-breaking moment, and sometimes additional special pieces. The process is carefully coordinated to create a precise and emotional moment.`,
        },
        {
          q: `How far in advance should you book a wedding DJ {city}?`,
          a: `At least six months in advance, and during peak season even a year ahead is recommended.`,
        },
        {
          q: `Is it possible to choose a full playlist?`,
          a: `Absolutely. You can provide a full song list, favorite songs, songs you prefer not to hear or general musical directions.`,
        },
        {
          q: `What are the most popular wedding attractions {city}?`,
          a: `Popular attractions include cold fireworks, heavy fog, exploding balloons, bubble machines, stage lighting, live musicians, confetti and more.`,
        },
      ],
  
      henna: [
        {
          q: `What does a henna DJ service include {city}?`,
          a: `The service includes personal consultation, a custom soundtrack, professional guidance throughout the process, sharp crowd reading, precise mixes and genre blending, sound and energy that guarantee a joyful henna celebration from start to finish.`,
        },
        {
          q: `What types of henna celebrations exist?`,
          a: `There are many styles such as Moroccan, Yemeni, Persian, Tunisian and more. In practice, styles can also be combined according to family traditions and the atmosphere you want to create.`,
        },
        {
          q: `How are entrances of the bride and groom planned for a henna {city}?`,
          a: `Entrances are planned in advance, including music selection and transition timing, to create an impressive and emotional moment. I guide you through song coordination and timing with the venue and family.`,
        },
        {
          q: `Can the henna ceremony itself be planned in an organized way?`,
          a: `Yes. The ceremony order, music for key moments and transition to the party are coordinated in advance to maintain good flow without energy drops.`,
        },
        {
          q: `How is the gold-gift ceremony incorporated into a henna?`,
          a: `Appropriate songs are chosen, timing is coordinated with the family and photographers, and the moment is kept respectful and emotional according to your preferences.`,
        },
        {
          q: `Can traditional clothing be incorporated into a henna {city}?`,
          a: `Absolutely. Outfit changes and timing are planned in advance to integrate smoothly with the ceremony and photography. Coordination with the venue or producer is recommended.`,
        },
        {
          q: `How much does a henna DJ cost {city}?`,
          a: `Prices typically range between ₪2,500–₪5,000 depending on the size, date, location and extras.`,
        },
        {
          q: `How do you choose a henna DJ {city}?`,
          a: `Choose a DJ who knows how to read the crowd, combine styles appropriate to henna traditions and lead the evening professionally.`,
        },
        {
          q: `How long does a DJ perform at a henna?`,
          a: `Performance duration is set according to the schedule and can range from a couple of hours to unlimited.`,
        },
        {
          q: `What do henna DJ packages include?`,
          a: `The basic package includes professional equipment and full performance. Upgrades may include custom booths, after-party sets, premium packages, live musicians and more.`,
        },
        {
          q: `Can drummers or live musicians be included in a henna?`,
          a: `Yes — drummers, shofars, saxophone, violin or bouzouki players can be incorporated.`,
        },
        {
          q: `What are popular henna attractions {city}?`,
          a: `Popular attractions include cold fireworks, heavy fog, exploding balloons, bubbles, stage lighting, confetti, drummers, magnet photographers, cocktail bars and more.`,
        },
        {
          q: `Does a DJ {city} also provide lighting equipment?`,
          a: `Most venues include basic lighting. Advanced lighting design can be added according to your preferences.`,
        },
        {
          q: `How do you build a henna playlist?`,
          a: `Musical directions are set together in advance, including favorite songs and songs to avoid, while adapting the music in real time to the audience.`,
        },
        {
          q: `Is a full playlist selection possible?`,
          a: `Yes — you can provide a full list, favorites, “do not play” list or general directions.`,
        },
        {
          q: `How do you adapt music for a diverse audience at a henna?`,
          a: `Through real-time crowd reading and smart mixing of styles, eras and rhythms.`,
        },
        {
          q: `What should be checked in the DJ contract?`,
          a: `Verify what is included, working hours, extension options, cancellation terms, venue requirements and backup availability.`,
        },
        {
          q: `Is backup equipment available?`,
          a: `Yes, a professional DJ arrives fully prepared with backup gear and contingency plans.`,
        },
      ],
      bar_bat: [
        {
          q: `What does a Bar/Bat Mitzvah DJ service include {city}?`,
          a: `The service includes personal consultation and building a soundtrack tailored especially for you, professional guidance throughout the entire process, sharp crowd reading, precise mixes and genre blending, sound and energy that guarantee a joyful Bar/Bat Mitzvah from the first moment to the final song.`,
        },
        {
          q: `How do you plan an impressive entrance for the celebrant at a Bar/Bat Mitzvah {city}?`,
          a: `Entrance planning includes selecting an entrance song, precise timing and positioning of the audience or family, sometimes combined with special effects. The goal is to create a powerful moment that raises the energy and sets the atmosphere.`,
        },
        {
          q: `Can speeches or blessings be included during a Bar/Bat Mitzvah {city}?`,
          a: `Yes. Timing is coordinated in advance, proper microphones are provided and sound clarity is ensured so the moment integrates smoothly without interrupting the flow of the event.`,
        },
        {
          q: `How do you keep the dance floor active for both kids and adults at a Bar/Bat Mitzvah?`,
          a: `By managing the set properly — segments for youth with current hits alongside music suitable for the whole family. Real-time crowd reading keeps the dance floor engaged.`,
        },
        {
          q: `How do you manage a youth dance floor so it doesn’t lose energy?`,
          a: `By maintaining musical continuity, smooth transitions between genres and adapting to the crowd’s energy — increasing intensity when needed and balancing when necessary.`,
        },
        {
          q: `Can games or interactive activities be included as part of the party?`,
          a: `Yes, according to your preference. Hosts, dancers or interactive activities can be added to match the celebrant’s style and audience.`,
        },
        {
          q: `How much does a Bar/Bat Mitzvah DJ cost {city}?`,
          a: `Prices typically range between ₪2,500–₪4,500 depending on event size, venue and selected extras.`,
        },
        {
          q: `How do you choose a Bar/Bat Mitzvah DJ {city}?`,
          a: `Choose a DJ experienced with younger audiences who can read the crowd and combine current music with songs suitable for all generations.`,
        },
        {
          q: `How do you choose songs for a Bar/Bat Mitzvah?`,
          a: `The celebrant selects favorite songs and musical directions in advance, while the DJ adapts the music during the event according to the atmosphere.`,
        },
        {
          q: `How long does a DJ perform at a Bar/Bat Mitzvah?`,
          a: `Performance duration is determined according to the schedule and can range from a few hours to unlimited.`,
        },
        {
          q: `What do Bar/Bat Mitzvah DJ packages include?`,
          a: `The basic package includes professional DJ equipment and performance throughout the event, with optional upgrades such as custom booths, after-parties, attractions and live musicians.`,
        },
        {
          q: `Can dancers or hosts be incorporated into a Bar/Bat Mitzvah?`,
          a: `Yes — dancers, hosts and interactive performers can boost energy and audience participation.`,
        },
        {
          q: `What are popular Bar/Bat Mitzvah attractions {city}?`,
          a: `Popular attractions include cold fireworks, heavy fog, exploding balloons, bubble machines, stage lighting, live musicians, confetti, magnet photographers, cocktail bars and more.`,
        },
        {
          q: `Does a DJ {city} also provide lighting equipment?`,
          a: `Most venues include basic lighting. Advanced lighting design can be added according to your preferences.`,
        },
        {
          q: `How do you adapt music for a diverse audience at a Bar/Bat Mitzvah?`,
          a: `By reading the crowd in real time and combining current hits with songs loved by both youth and adults.`,
        },
        {
          q: `Is a full playlist selection possible?`,
          a: `Yes — you can provide a full list, favorites or general directions, while the DJ adapts in real time.`,
        },
        {
          q: `What should be checked in the DJ contract?`,
          a: `Verify what is included, working hours, extension options, cancellation terms, venue requirements and backup availability.`,
        },
        {
          q: `Is backup equipment available?`,
          a: `Yes, a professional DJ arrives with backup gear and contingency plans.`,
        },
        {
          q: `How far in advance should you book a Bar/Bat Mitzvah DJ?`,
          a: `It is recommended to book at least 3–6 months in advance, and earlier for popular dates.`,
        },
        {
          q: `Can video or presentations be incorporated into a Bar/Bat Mitzvah?`,
          a: `Yes, screens can be used to display clips, presentations and personal photos during the event.`,
        },
      ],
  
      event: [
        {
          q: `What does a DJ service for a corporate or private event include {city}?`,
          a: `The service includes personal consultation, a custom soundtrack, professional guidance, sharp crowd reading, precise mixes and genre blending, sound and energy that guarantee a successful event from start to finish.`,
        },
        {
          q: `How much does a DJ for an event cost {city}?`,
          a: `Prices typically range between ₪2,000–₪5,000 depending on event type, size, venue and selected extras.`,
        },
        {
          q: `How do you choose a DJ for an event?`,
          a: `Choose a DJ with experience, strong crowd-reading ability, proven reviews and a musical style that fits the event.`,
        },
        {
          q: `How long does a DJ perform at an event?`,
          a: `Performance duration is determined in advance and can range from a couple of hours to unlimited.`,
        },
        {
          q: `What do DJ packages for events include?`,
          a: `The basic package includes professional equipment and performance throughout the event, with optional upgrades such as custom booths, after-parties, attractions and live musicians.`,
        },
        {
          q: `Is a DJ suitable for small events as well?`,
          a: `Yes — the service can be tailored for both intimate gatherings and large-scale events.`,
        },
        {
          q: `Does a DJ {city} also provide lighting equipment?`,
          a: `Most venues include basic lighting. Advanced lighting and stage design can be added as needed.`,
        },
        {
          q: `What are popular attractions for events {city}?`,
          a: `Popular attractions include cold fireworks, heavy fog, exploding balloons, bubbles, stage lighting, live musicians, confetti, drummers, magnet photographers, cocktail bars and more.`,
        },
        {
          q: `Can an event be held outdoors or at a special location?`,
          a: `Yes — outdoor events such as beach or garden parties are possible but require special sound, lighting and equipment planning.`,
        },
        {
          q: `How do you adapt music for a diverse audience at an event?`,
          a: `Through real-time crowd reading and smart mixing of genres and eras to keep the dance floor active.`,
        },
        {
          q: `Is a full playlist selection possible?`,
          a: `Yes — you can provide song lists or general directions while the DJ adapts to the crowd.`,
        },
        {
          q: `Can live musicians or performances be included?`,
          a: `Yes — saxophonists, violinists, drummers and other performers can be incorporated.`,
        },
        {
          q: `How far in advance should you book a DJ?`,
          a: `It is recommended to book at least 3–6 months in advance, and earlier for peak dates.`,
        },
        {
          q: `What should be checked in the DJ contract?`,
          a: `Verify included services, working hours, extension options, cancellation terms and backup availability.`,
        },
        {
          q: `Is backup equipment available?`,
          a: `Yes, professional DJs always arrive with backup gear.`,
        },
        {
          q: `Can video or presentations be incorporated into an event?`,
          a: `Yes, screens can display videos, presentations and personalized content.`,
        },
      ],
  
      homeEvent: [
        {
          q: `What does a DJ service for an event include?`,
          a: `The service includes personal consultation, a custom soundtrack, professional guidance, sharp crowd reading, precise mixes and genre blending, sound and energy that guarantee a successful event from start to finish.`,
        },
        {
          q: `How much does a DJ for an event cost?`,
          a: `Prices typically range between ₪2,000–₪5,000 depending on event type, size, location and extras.`,
        },
        {
          q: `What types of events do you perform at as a DJ?`,
          a: `Weddings, henna celebrations, Bar/Bat Mitzvahs, corporate events, staff parties, team-building events, private parties and family events.`,
        },
        {
          q: `How do you adapt music to the audience?`,
          a: `By reading the crowd in real time and combining genres, eras and tempos to maintain a lively dance floor.`,
        },
        {
          q: `Do you provide your own DJ and sound equipment?`,
          a: `Yes — I arrive with professional DJ gear including speakers, mixer and microphones tailored to the venue.`,
        },
        {
          q: `Can lighting also be added?`,
          a: `Most venues include basic lighting. Advanced lighting design can be added according to your preferences.`,
        },
        {
          q: `How far in advance should you book a DJ?`,
          a: `It is recommended to book several months in advance, especially for peak dates.`,
        },
        {
          q: `Are DJs suitable for small and intimate events?`,
          a: `Absolutely — sound level, music style and atmosphere are adapted for smaller gatherings as well.`,
        },
        {
          q: `How do you choose a DJ for an event?`,
          a: `Check experience, reviews, event videos, musical style and personal connection.`,
        },
        {
          q: `What do packages and upgrade options include?`,
          a: `The basic package includes DJ equipment and performance, with optional upgrades such as custom booths, after-parties, attractions and live musicians.`,
        },
        {
          q: `What are popular attractions for events?`,
          a: `Cold fireworks, heavy fog, balloons, bubbles, lighting, musicians, confetti and more.`,
        },
        {
          q: `Is backup equipment available in case of malfunction?`,
          a: `Yes — professional DJs always arrive with backup equipment.`,
        },
        {
          q: `Do you perform at corporate events and team-building days?`,
          a: `Yes — music is adapted to the organization and audience.`,
        },
        {
          q: `How do you build a playlist for an event?`,
          a: `We define musical directions, key songs and preferences, then create a dynamic set for the event.`,
        },
        {
          q: `Can we send a song list in advance?`,
          a: `Absolutely — you can send must-play songs and songs to avoid.`,
        },
        {
          q: `What happens if the event runs longer?`,
          a: `Performance time can be extended by arrangement and additional hourly fee.`,
        },
        {
          q: `Is there coordination before the event?`,
          a: `Yes — a meeting or call is recommended to review schedule, music style and key moments.`,
        },
      ],
    }, 
  },

// בתוך en
geo: {
  regions: {
    דרום: 'South',
    מרכז: 'Central',
  },

  /**
   * Cities — keys are the Hebrew SSOT city names from src/constants/geo.ts
   * Values are English UI labels (transliteration / common spelling).
   */
  cities: {
    'באר שבע': "Be'er Sheva",
    'אשקלון': 'Ashkelon',
    'אשדוד': 'Ashdod',
    'קריית גת': 'Kiryat Gat',
    'אופקים': 'Ofakim',
    'נתיבות': 'Netivot',
    'שדרות': 'Sderot',
    'להבים': 'Lehavim',
    'עומר': 'Omer',
    'דימונה': 'Dimona',
    'קריית מלאכי': 'Kiryat Malakhi',
    'ערד': 'Arad',
    'ירוחם': 'Yeruham',
    'מיתר': 'Meitar',
    'אילת': 'Eilat',
    'קריית עקרון': 'Kiryat Ekron',
    'גן יבנה': 'Gan Yavne',

    'תל אביב': 'Tel Aviv',
    'רמת גן': 'Ramat Gan',
    'גבעתיים': 'Givatayim',
    'ראשון לציון': 'Rishon LeZion',
    'חולון': 'Holon',
    'בת ים': 'Bat Yam',
    'רעננה': "Ra'anana",
    'הרצליה': 'Herzliya',
    'פתח תקווה': 'Petah Tikva',
    'יבנה': 'Yavne',
    'רחובות': 'Rehovot',
    'נס ציונה': 'Ness Ziona',
    'כפר סבא': 'Kfar Saba',
    'הוד השרון': 'Hod HaSharon',
    'מודיעין': "Modi'in",
    'ראש העין': "Rosh HaAyin",
    'נתניה': 'Netanya',
    'חדרה': 'Hadera',
    'עמק חפר': 'Hefer Valley',
  },

  /**
   * Fallback for global pages (city = '')
   * Keep it simple for UI usage.
   */
  globalLabel: 'Israel',
},
} as const;

export type EnPack = typeof en;
